import React, { useEffect } from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import styles from "./read.module.css";
import SignupForm from "./components/SignupForm";

const Check = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF9A80" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>
);

const features = [
  {
    title: "Maintenance management",
    text: "Submit, assign and track work orders from submission to close. Never lose a ticket again.",
    bg: "#FDECE7",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C94A31" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5Z"></path></svg>,
  },
  {
    title: "Resident communication",
    text: "Unified inbox for texts, emails and portal messages — one thread per resident.",
    bg: "#EEEAFB",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4B2FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z"></path></svg>,
  },
  {
    title: "Smart device control",
    text: "Monitor and control locks, thermostats and sensors across every unit, all from one place.",
    bg: "#FFF4DE",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9A6400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z"></path></svg>,
  },
  {
    title: "Lease renewals",
    text: "Track every expiring lease, send offers in bulk and sign renewals before a unit goes vacant.",
    bg: "#E5F4F1",
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1F7A68" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"></rect><path d="M16 2v4M8 2v4M3 10h18"></path></svg>,
  },
];

const steps = [
  { title: "Leave your name and email", text: "It takes ten seconds. No credit card or sales call required to get on the list." },
  { title: "We set you up", text: "Our team reaches out to learn about your portfolio and prepares your test-drive account." },
  { title: "Take it for a spin", text: "Explore work orders, messaging and smart devices — and see how much time it gives back." },
];

const Read = () => {
  useEffect(() => {
    // The page breaks out of the narrow Custom layout with 100vw; hide the scrollbar-width overflow
    const prev = document.body.style.overflowX;
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = prev;
    };
  }, []);

  const scrollToSignup = () => {
    const el = document.getElementById("signup");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Test drive PropertyDek</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      {/* HERO */}
      <section className={styles.Hero}>
        <div className={styles.Container}>
          <nav className={styles.Nav}>
            <Link to="/"><img src="/static/img/logo.svg" alt="PropertyDek" className={styles.LogoWhite} height="34" /></Link>
          </nav>

          <div className={styles.HeroBody}>
            <div className={styles.HeroText}>
              <div className={styles.Badge}>Early access for READ members</div>
              <h1 className={styles.HeroTitle}>Take PropertyDek<br /><span>for a test drive.</span></h1>
              <p className={styles.HeroLead}>Run maintenance, resident messaging, smart devices and renewals from one place — built for affordable and workforce housing portfolios under 1,000 doors. Leave your name and email and we'll set you up to try it first-hand.</p>
              <div className={styles.CheckList}>
                <div><Check /><span>A guided walkthrough with our team</span></div>
                <div><Check /><span>Hands-on access to the platform and resident app</span></div>
                <div><Check /><span>No credit card, no commitment</span></div>
              </div>
            </div>

            <div id="signup" className={styles.SignupCard}>
              <SignupForm />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL TRY */}
      <section className={styles.Section}>
        <div className={`${styles.Container} ${styles.SectionInner}`}>
          <div className={styles.SectionHead}>
            <div className={styles.Eyebrow}>What you'll try</div>
            <h2 className={styles.SectionTitle}>Everything your operations team needs. Nothing they don't.</h2>
            <p className={styles.SectionSub}>Your test drive covers the tools property managers use every day.</p>
          </div>
          <div className={styles.FeatureGrid}>
            {features.map((f) => (
              <div key={f.title} className={styles.FeatureCard}>
                <div className={styles.FeatureIcon} style={{ background: f.bg }}>{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className={`${styles.Section} ${styles.SectionAlt}`}>
        <div className={`${styles.Container} ${styles.SectionInner}`}>
          <div className={styles.SectionHead}>
            <div className={styles.Eyebrow}>How it works</div>
            <h2 className={styles.SectionTitle}>From sign-up to hands-on in three steps</h2>
          </div>
          <div className={styles.StepGrid}>
            {steps.map((s, i) => (
              <div key={s.title} className={styles.StepCard}>
                <div className={styles.StepNum}>{i + 1}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={styles.Cta}>
        <div className={styles.CtaInner}>
          <h2 className={styles.CtaTitle}>Smarter operations.<br /><span>Stronger communities.</span></h2>
          <p className={styles.CtaSub}>Be one of the first teams to try PropertyDek.</p>
          <button type="button" className={styles.CtaBtn} onClick={scrollToSignup}>
            <span>Reserve my test drive</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={styles.Footer}>
        <div className={`${styles.Container} ${styles.FooterInner}`}>
          <img src="/static/img/logo.svg" alt="PropertyDek" className={styles.LogoWhite} height="28" />
          <div className={styles.FooterLinks}>
            <a href="tel:+18339250003">(833) 925-0003</a>
            <a href="mailto:hello@propertydek.com">hello@propertydek.com</a>
            <span>© {new Date().getFullYear()} PropertyDek. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Read;
